import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button, controlToastState, openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";

import {
  uploadAuthLetter,
  UploadAuthLetterPayload,
} from "../../../../api/postCall/uploadAuthLetter";
import DocViewerModalwithTitle from "../../../../components/modals/docViewerModalwithTitle/docViewerModalwithTitle";
import NarLetterModal from "../../../../components/modals/narLetterModal/narLetterModal";
import TriggerRpaWithDocListModal from "../../../../components/modals/triggerRpaWithDocListModal/triggerRpaWithDocListModal";
import { authStatusForDisablingTriggerRpa } from "../../../../constants/authStatusForDisablingTriggerRpa";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { JCodeDocuments } from "../../../../data-model/medicalPaOrdersModel";
import { DocWriteBackStatus } from "../../../../enums/evBvWriteBackStatus";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { getDocTypeFromOrgStrict } from "../../../../utils/getDocFromOrg";
import { removeItemFromLocalStorage } from "../../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { getAllUniqueDocsFromJCodesInFlattenedArray } from "../../../singleOrderLayout/utils/docsForRpa";
import { getAllJCodes } from "../../../singleOrderLayout/utils/existingDocsToLs";
import {
  CptCodesTableColumnsKeys,
  generateCptCodesTableData,
} from "./documentsTable";

const Documents = () => {
  const { orderId } = useParams();
  const { singleMedicalPaOrder, isLoading } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { hasCptTableAnyError, shouldDisableMarkAsComplete } =
    useBusniessOfficeNotesContext();

  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const [tableData, setTableData] = useState<any[]>([]);
  const [rpaButtonDisabled, setRpaButtonDisabled] = useState(true);
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const lastExecutionTimeRef = useRef<number>(0);

  const getLocalStorageKey = () => `cptCodes_${orderId}`;

  const saveTableDataToLocalStorage = (data: any[]) => {
    try {
      const key = getLocalStorageKey();
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  useEffect(() => {
    return () => {
      removeItemFromLocalStorage("medicineNames");
    };
  }, []);

  useEffect(() => {
    if (loading) {
      setRpaButtonDisabled(true);
      return;
    }

    if (
      singleMedicalPaOrder?.status?.document_upload_status ===
      DocWriteBackStatus.IN_PROGRESS
    ) {
      setRpaButtonDisabled(true);
      return;
    }
    if (!jcodes || jcodes?.length === 0) {
      setRpaButtonDisabled(true);
      return;
    }
    setRpaButtonDisabled(false);
  }, [loading, singleMedicalPaOrder]);

  useEffect(() => {
    if (openedModalId === ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL) {
      openModal(ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL);
    }
    if (openedModalId === ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG) {
      openModal(ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (singleMedicalPaOrder) {
      let generatedData = generateCptCodesTableData(singleMedicalPaOrder);

      if (orderId === "7d5b393e-83de-4380-b5e4-90c82acc4620") {
        generatedData = generatedData.map((row) => ({
          ...row,
          startFrom: "10/05/2024",
          endAt: "10/05/2025",
        }));
      }

      setTableData(generatedData);
    }
  }, [singleMedicalPaOrder, orderId]);

  const jcodes = useMemo(
    () => getAllJCodes(singleMedicalPaOrder ?? {}),
    [singleMedicalPaOrder],
  );

  const handleTableRowUpdate = (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => {
    setTableData((prevData) => {
      const updatedData = [...prevData];
      if (updatedData[rowIndex]) {
        updatedData[rowIndex] = {
          ...updatedData[rowIndex],
          [fieldKey]: newValue,
        };
      }

      saveTableDataToLocalStorage(updatedData);

      return updatedData;
    });
  };

  const handleSaveChanges = async (selectedDocuments: JCodeDocuments[]) => {
    const currentTime = Date.now();
    const timeSinceLastExecution = currentTime - lastExecutionTimeRef.current;
    if (timeSinceLastExecution < 10000) {
      controlToastState("retry-error");
      return;
    }

    lastExecutionTimeRef.current = currentTime;

    const extractFlattenDocsWithUniqueDocRefsPath = selectedDocuments
      .map((doc) => ({
        document_type:
          getDocTypeFromOrgStrict(getOrgIdForFetchExternalWorklist()) ||
          "Unknown Document Type",
        document_name: doc?.doc_title ?? "",
        document_path: doc?.doc_path ?? "",
      }))
      .filter(
        (doc, index, self) =>
          index ===
          self.findIndex((t) => t.document_path === doc.document_path),
      );

    const payloadForRpa: UploadAuthLetterPayload = {
      identifier: singleMedicalPaOrder?.id ?? "",
      patient_mrn: singleMedicalPaOrder?.demographics?.patient_mrn ?? "",
      documents: extractFlattenDocsWithUniqueDocRefsPath,
      org_id: getOrgIdForFetchExternalWorklist() || "",
    };

    try {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.DOCUMENT_UPLOAD_INITIATED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
          payer_name: payerName,
        },
      });
      setLoading(true);
      await Promise.race([
        uploadAuthLetter(payloadForRpa),
        new Promise((resolve) =>
          setTimeout(() => resolve({ success: true }), 2000),
        ),
      ]);
      dispatch(closeModal());
      controlToastState("documents-updated-success");
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const shouldHideTriggerRpaButton = useMemo(() => {
    return authStatusForDisablingTriggerRpa.includes(
      singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
    );
  }, [singleMedicalPaOrder?.auth_on_file?.auth_status]);

  useEffect(() => {
    if (openedModalId === ModalId.TRIGGER_RPA_MODAL) {
      openModal(ModalId.TRIGGER_RPA_MODAL);
    }
    if (openedModalId === ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG) {
      openModal(ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG);
    }
  }, [openedModalId]);

  return (
    <div className="cpt-codes-table--container flex h-full flex-col">
      {!shouldHideTriggerRpaButton && !shouldDisableMarkAsComplete && (
        <div className="trigger-rpa ml-auto w-fit">
          {" "}
          <Button
            buttonType="secondary"
            size="medium"
            onClick={() => {
              apiWorkerManager.enqueueAndProcess({
                patient_id: singleMedicalPaOrder?.patient_id ?? "",
                order_id: singleMedicalPaOrder?.order_id ?? "",
                org_id: getOrgIdForFetchExternalWorklist() || "",
                event_name: EventsName.TRIGGER_RPA_CLICKED,
                user_id: user?.id ?? "",
                user_email: user?.email ?? "",
                additional_data: {
                  screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
                  payer_name: payerName,
                },
              });
              dispatch(
                setOpenedModalId({
                  id: ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL,
                }),
              );
            }}
            disabled={rpaButtonDisabled || hasCptTableAnyError}
          >
            Trigger RPA
          </Button>
        </div>
      )}
      {isLoading ? (
        <LoaderMessage message={"Fetching CPT Codes"} />
      ) : (
        <>
          <CustomTable
            tableHeaders={CptCodesTableColumnsKeys}
            tableData={tableData}
            itemsPerPage={tableData.length}
            pagesPerView={5}
            count={tableData.length}
            onRowClick={() => {}}
            tableName="cptCodes"
            hideSearchBar={true}
            hidePagination={true}
            onTableRowUpdate={handleTableRowUpdate}
          />
          {openedModalId === ModalId.NAR_LETTER_MODAL && (
            <NarLetterModal
              onClose={() => {
                dispatch(closeModal());
              }}
              showDragDropUpload={true}
              shouldLogEvent={true}
            />
          )}
          {openedModalId === ModalId.DOC_VIEWER_MODAL_WITH_TITLE && (
            <DocViewerModalwithTitle
              onClose={() => {
                dispatch(closeModal());
              }}
            />
          )}
          {openedModalId === ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL && (
            <TriggerRpaWithDocListModal
              onTriggerRpa={handleSaveChanges}
              onClose={() => {
                dispatch(closeModal());
              }}
              onCancel={() => {
                dispatch(closeModal());
              }}
              loading={loading}
              orderId={orderId ?? ""}
              docList={getAllUniqueDocsFromJCodesInFlattenedArray(
                singleMedicalPaOrder ?? {},
              )}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Documents;
